import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Button } from '@abyss/web/ui/Button';
import { Divider } from '@abyss/web/ui/Divider';
import { Heading } from '@abyss/web/ui/Heading';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { V2Popover } from '@abyss/web/ui/Popover';
import { RadioGroup } from '@abyss/web/ui/RadioGroup';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { phoneOnly } from '../../../../../common/ConstantsStyles';
import { ContentWrapper } from '../../../../../common/ContentWrapper';
import { ImageComponent } from '../../../../../common/ImageComponent';
import { Breadcrumb } from '../../../../../common/PSXHeader/Breadcrumb';
import { truncateString } from '../../../../../common/Widget/SmartChoiceWidget/utils';
import { useAdobePageTrackEvent } from '../../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { NetworkAffiliationDetails } from '../../../../../models/ProviderDetails';
import {
  capitalizeFirstLetters,
  normalizeMedicalGroupName,
} from '../../../../../utils/providerNames.utils';
import {
  StyledAffiliationCard,
  StyledAffiliationDetailsWrapper,
  StyledEnrollmentIdWrapper,
  StyledMedicalGroupsList,
  StyledNetworkAffiliationContainer,
  StyledRadioGroupWrapper,
  StyledViewListPopoverWrapper,
} from './ChooseNetworkAffiliation.styled';
import {
  ButtonRowBox,
  ChoosePCPLocationAvatar,
  PCPCardWrapper,
  PCPHeaderContainer,
  SaveAndCancelButton,
  SelectedPCPCard,
  StyledProviderName,
} from './ChoosePCPLocation.styled';

type Props = {
  breadcrumbs: any[];
  handleBackButtonClick: () => void;
  handleContinue: (
    selectedAffiliation: NetworkAffiliationDetails | null
  ) => void;
  imageLocation?: string;
  networkAffiliations: NetworkAffiliationDetails[];
  providerName: string;
  providerType: string;
  specialty?: string;
  isDeleteButtonVisible?: boolean;
  handleDelete?(): void;
  selectedProviderAffiliation?: NetworkAffiliationDetails | null;
};

export const ChooseNetworkAffiliation = ({
  breadcrumbs,
  handleBackButtonClick,
  handleContinue,
  imageLocation,
  networkAffiliations,
  providerName,
  providerType,
  specialty,
  isDeleteButtonVisible,
  handleDelete,
  selectedProviderAffiliation,
}: Props) => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const { t } = useTranslation();
  const uiMessaging = (
    useLagoon('ui-messaging') as () => Array<{ key: string; message: string }>
  )();
  const isSingleAffiliation = networkAffiliations.length === 1;
  const imgRef = useRef<HTMLImageElement>(null);

  const getUiMessage = (key: string, fallback: string): string =>
    find(uiMessaging, { key })?.message || fallback;

  const chooseNetworkAffiliationText = getUiMessage(
    'NETWORK_AFFILIATION_CHOOSE_NETWORK_AFFILIATION',
    t('NETWORK_AFFILIATION.CHOOSE_NETWORK_AFFILIATION')
  );
  const availableText = getUiMessage(
    'NETWORK_AFFILIATION_AVAILABLE',
    t('NETWORK_AFFILIATION.AVAILABLE')
  );
  const singleAffiliationMessage = getUiMessage(
    'NETWORK_AFFILIATION_SINGLE_AFFILIATION_MESSAGE',
    ''
  );
  const selectAffiliationMessage = getUiMessage(
    'NETWORK_AFFILIATION_SELECT_AFFILIATION',
    ''
  );
  const networkAffiliationInfoTitle = getUiMessage(
    'NETWORK_AFFILIATION_INFO_TITLE',
    t('NETWORK_AFFILIATION.NETWORK_AFFILIATION_INFO_TITLE')
  );
  const networkAffiliationInfoMessage = getUiMessage(
    'NETWORK_AFFILIATION_INFO',
    ''
  );

  const [selectedIndex, setSelectedIndex] = useState<string | null>(
    isSingleAffiliation ? '0' : null
  );

  useEffect(() => {
    if (selectedProviderAffiliation) {
      const matchIndex = networkAffiliations.findIndex(
        (a) =>
          a.networkAffiliationId ===
          selectedProviderAffiliation.networkAffiliationId
      );
      setSelectedIndex(matchIndex >= 0 ? matchIndex.toString() : null);
    } else if (!isSingleAffiliation) {
      setSelectedIndex(null);
    }
  }, [selectedProviderAffiliation, networkAffiliations, isSingleAffiliation]);

  const chooseNetworkAffiliation: string = 'body:choose network affiliation';

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'choose network affiliation',
    sitesectionLevel1: 'pcp',
  });

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  const handleBack = () => {
    adobeLinkTrackEvent({
      name: 'back',
      location: chooseNetworkAffiliation,
      type: 'internal',
    });
    handleBackButtonClick();
  };

  const handleContinueClick = () => {
    adobeLinkTrackEvent({
      name: 'continue',
      location: chooseNetworkAffiliation,
      type: 'internal',
    });
    const selected =
      selectedIndex !== null
        ? networkAffiliations[Number(selectedIndex)]
        : null;
    handleContinue(selected);
  };

  const isContinueDisabled = selectedIndex === null;

  const buttonRow = (
    <Layout.Group alignLayout={mobileScreen ? 'left' : 'right'} space={12}>
      <Button
        data-testid="network-affiliation-back-button"
        onClick={handleBack}
        variant="outline"
      >
        {t('BACK')}
      </Button>
      {isDeleteButtonVisible && (
        <SaveAndCancelButton
          className="cancel-button"
          data-auto-testid="choose-pcp-cancel"
          data-testid="choose-pcp-cancel"
          onClick={handleDelete}
          variant="outline"
        >
          {t('DELETE')}
        </SaveAndCancelButton>
      )}
      <Button
        data-testid="network-affiliation-continue-button"
        isDisabled={isContinueDisabled}
        onClick={handleContinueClick}
      >
        {t('CHOOSE_PCP_FLOW.SAVE_SELECTION')}
        <IconSymbol color="$white" icon="chevron_right" size="16px" />
      </Button>
    </Layout.Group>
  );

  const renderMedicalGroup = (medicalGroupNames: string[]) => {
    const normalizedNames = medicalGroupNames.map(
      (name) => normalizeMedicalGroupName(name).value
    );
    return (
      <Layout.Group
        css={{
          'abyss-layout-group': {
            alignItems: mobileScreen ? 'baseline' : undefined,
          },
        }}
        space={4}
      >
        <Text
          css={{
            'abyss-text-root': mobileScreen
              ? {
                  whiteSpace: 'nowrap',
                  lineHeight: '24px',
                }
              : {},
          }}
          fontWeight={600}
          size="12px"
        >
          {t('NETWORK_AFFILIATION.MEDICAL_GROUP')}:
        </Text>
        <Text fontWeight={400} size="12px">
          {normalizedNames.length
            ? normalizedNames.length === 1
              ? normalizedNames[0]
              : truncateString(normalizedNames.join(', '), 24)
            : t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE')}
        </Text>
        {medicalGroupNames.length > 1 && (
          <StyledViewListPopoverWrapper>
            <V2Popover
              content={
                <Layout.Stack alignItems="left" space={8}>
                  <Text fontWeight="600" size="16px">
                    {t('NETWORK_AFFILIATION.MEDICAL_GROUPS')}
                  </Text>
                  <StyledMedicalGroupsList>
                    {normalizedNames.map((name, idx) => (
                      <li key={name}>
                        <Text color="$neutralGray6" key={idx} size="16px">
                          {name}
                        </Text>
                      </li>
                    ))}
                  </StyledMedicalGroupsList>
                </Layout.Stack>
              }
              position="bottom"
            >
              <Text
                aria-label={t('NETWORK_AFFILIATION.VIEW_LIST_ARIA_LABEL')}
                color="$interactive1"
                css={{
                  'abyss-text-root': { whiteSpace: 'nowrap' },
                }}
                fontWeight={600}
                size="12px"
              >
                {t('NETWORK_AFFILIATION.VIEW_LIST')}
              </Text>
            </V2Popover>
          </StyledViewListPopoverWrapper>
        )}
      </Layout.Group>
    );
  };

  const renderAffiliationCard = (
    affiliation: NetworkAffiliationDetails,
    index: number
  ) => {
    const isSelected =
      isSingleAffiliation ||
      (selectedIndex !== null && index === Number(selectedIndex));
    const medicalGroupNames = affiliation.medicalgrpaffilIdName || [];
    const MedicalGroupLayout = (
      mobileScreen ? Layout.Stack : Layout.Group
    ) as React.ComponentType<{
      alignItems?: string;
      space?: number;
      children: React.ReactNode;
    }>;
    const medicalGroupLayoutProps = mobileScreen
      ? { alignItems: 'flex-start', space: 8 }
      : { alignLayout: 'left', space: 32 };

    return (
      <StyledAffiliationCard
        data-testid={`network-affiliation-card-${index}`}
        key={affiliation.networkAffiliationId}
        readOnly={isSingleAffiliation}
        selected={isSelected}
      >
        <RadioGroup.Radio
          css={{
            'abyss-radio-root': {
              cursor: 'pointer',
            },
            'abyss-radio-label': {
              cursor: 'pointer',
              fontWeight: '700',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              lineHeight: '1.25',
            },
            'abyss-radio': {
              // size not working in Abyss for radio, so using css to override
              width: '20px',
              height: '20px',
              minWidth: '20px',
              minHeight: '20px',
              cursor: 'pointer',
            },
            ...(mobileScreen && {
              'abyss-radio-label': { whiteSpace: 'normal' },
            }),
          }}
          label={capitalizeFirstLetters(affiliation.networkAffiliationName)}
          size="$sm"
          value={index.toString()}
        />

        <Divider
          color="$gray3"
          css={{
            'abyss-divider-root': {
              marginLeft: '28px',
              width: 'calc(100% - 28px) !important',
            },
          }}
          height={1}
          margin={12}
        />

        <StyledAffiliationDetailsWrapper>
          <MedicalGroupLayout {...medicalGroupLayoutProps}>
            <Text fontWeight={600} size="12px">
              {t('Accepting new patients')}
            </Text>

            <StyledEnrollmentIdWrapper>
              <Text fontWeight={600} size="12px">
                {t('NETWORK_AFFILIATION.ENROLLMENT_ID')}
                {': '}
              </Text>
              <Text fontWeight={400} size="12px">
                {affiliation.networkAffiliationId}
              </Text>
            </StyledEnrollmentIdWrapper>

            {renderMedicalGroup(medicalGroupNames)}
          </MedicalGroupLayout>
        </StyledAffiliationDetailsWrapper>
      </StyledAffiliationCard>
    );
  };

  return (
    <div style={{ overflow: 'initial', position: 'relative' }}>
      <PCPHeaderContainer
        data-auto-testid="choose-network-affiliation-container"
        data-testid="choose-network-affiliation-container"
      >
        <ContentWrapper>
          <PCPCardWrapper>
            <Breadcrumb
              breadcrumbs={breadcrumbs}
              onBackButtonClick={handleBack}
            />
            <SelectedPCPCard height="66px" padding="10px">
              <Layout.Group
                css={{
                  'abyss-layout-group': {
                    display: 'flex',
                    alignItems: 'flex-start',
                  },
                }}
              >
                <ChoosePCPLocationAvatar>
                  <ImageComponent
                    imgRef={imgRef}
                    imgSrc={imageLocation}
                    providerType={providerType}
                    sizeToken="$choosePCPLocationIconSize"
                  />
                </ChoosePCPLocationAvatar>
                <Layout.Stack alignItems="left" space={0}>
                  <Text
                    css={{
                      color: '#323334',
                      '@screen < $md': { color: '#FFFFFF' },
                    }}
                    fontWeight="bold"
                    size="lg"
                  >
                    <StyledProviderName>{providerName}</StyledProviderName>
                  </Text>
                  {specialty ? (
                    <Text
                      css={{
                        fontSize: '12.64px',
                        fontWeight: '500',
                        color: '#4B4D4F',
                        '@screen < $md': { color: '#FFFFFF' },
                      }}
                      size="xs"
                    >
                      {specialty}
                    </Text>
                  ) : null}
                </Layout.Stack>
              </Layout.Group>
            </SelectedPCPCard>
          </PCPCardWrapper>
        </ContentWrapper>
      </PCPHeaderContainer>
      <ContentWrapper>
        <StyledNetworkAffiliationContainer
          alignItems="left"
          grow={mobileScreen}
          space={mobileScreen ? 16 : 32}
        >
          <Layout.Stack alignItems="left" grow={mobileScreen} space={8}>
            <Heading display="h3">{chooseNetworkAffiliationText}</Heading>
            <Text color="$neutralGray6" fontWeight={700} size="18px">
              {networkAffiliations.length} {availableText}
            </Text>
            <Text color="$neutralGray6" size="18px">
              {isSingleAffiliation
                ? singleAffiliationMessage
                : selectAffiliationMessage}
            </Text>
            <V2Popover
              content={networkAffiliationInfoMessage}
              position="bottom"
            >
              <Layout.Group alignItems="center" space={4}>
                <IconSymbol icon="info" size="16" variant="outlined" />
                <Text
                  color="$interactive1"
                  css={{ marginLeft: 4 }}
                  fontWeight={600}
                  size="$sm"
                >
                  {networkAffiliationInfoTitle}
                </Text>
              </Layout.Group>
            </V2Popover>
          </Layout.Stack>

          <RadioGroup
            data-testid="network-affiliation-radio-group"
            label=""
            onChange={(e) => setSelectedIndex(e.target.value)}
            value={selectedIndex ?? ''}
          >
            <StyledRadioGroupWrapper>
              {networkAffiliations.map(renderAffiliationCard)}
            </StyledRadioGroupWrapper>
          </RadioGroup>

          {!mobileScreen && buttonRow}
        </StyledNetworkAffiliationContainer>
      </ContentWrapper>
      {mobileScreen && (
        <ButtonRowBox color="$white" padding="0">
          {buttonRow}
        </ButtonRowBox>
      )}
    </div>
  );
};
